import { css } from '@styled-system/css';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useAnalysisSegment } from '@/features/analysisPage/contexts/AnalysisSegmentContext';
import { useVestiaireLocked } from '@/features/subscription/hooks/useVestiaireAccess';
import { ProductSalesFeed } from '@/shared/components/analysis/ProductSalesFeed';
import { RelaunchSearchModal } from '@/shared/components/analysis/RelaunchSearchModal';
import { Button } from '@/shared/components/ui/Button';
import { SearchBarNew } from '@/shared/components/ui/SearchBarNew';
import { useCreateAnalysisFromCriteria, useCreateVestiaireAnalysis } from '@/shared/hooks/analysis/mutations';
import { useGetAnalysisByIdWithCache } from '@/shared/hooks/analysis/useGetAnalysisByIdWithCache';
import {
  ProductStatus,
  useGetAvailableBrandsQuery,
  useMoveAnalysisToFolderMutation,
  useMoveAnalysisToPositionMutation,
} from '@/shared/hooks/queries/generated';
import { ProductItemData } from '@/shared/types/product';
import { analysisCriteriaFilter, canSearchSimilarItems, isVestiaireAnalysis } from '@/shared/utils/analysis/platform';
import { buildSimilarItemCriteria, buildVestiaireSimilarItemCriteria } from '@/shared/utils/analysis/relaunchCriteria';
import { hasSimilaritySource } from '@/shared/utils/analysis/similarity';

import { useProductFeedData } from '../../../features/analysisPageSellsHist/hooks/useProductFeedData';
import { useProductFeedInfinite } from '../../../features/analysisPageSellsHist/hooks/useProductFeedInfinite';
import type { ProductFilters } from '../../../features/analysisPageSellsHist/types';

import { SellHistoryFilters as FiltersComponent } from './SellHistoryFilters';

interface ProductFeedProps {
  status: ProductStatus;
  /** Mode statique (drawer niche) : liste fournie, pas de pagination serveur. */
  staticProducts?: ProductItemData[];
  isLoading?: boolean;
  /** Rendu en tête de la toolbar (ex. le sélecteur Ventes/Retirés/Invendus). */
  toolbarLeading?: ReactNode;
}

/**
 * Feed produits : par défaut paginé serveur (scroll infini, segment appliqué
 * côté serveur). Le mode `staticProducts` (drawer de niche) garde l'ancien
 * fonctionnement 100 % client.
 */
export const ProductFeed: FC<ProductFeedProps> = ({ status, staticProducts, isLoading = false, toolbarLeading }) => {
  const navigate = useNavigate();
  const { id: currentAnalysisId = '' } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { createAnalysisFromCriteria } = useCreateAnalysisFromCriteria();
  const { createVestiaireAnalysis } = useCreateVestiaireAnalysis();
  // Filtre de création de l'analyse courante (cache) — base du zoom « similaires »
  const { data: parentAnalysisData } = useGetAnalysisByIdWithCache({ id: currentAnalysisId });
  const parentAnalysis = parentAnalysisData?.getAnalysisById;
  // Plateforme lue sur l'ANALYSE et non sur useAnalysisSegment() : ce feed est aussi rendu HORS
  // AnalysisSegmentProvider (page publique), où le contexte vaut 'vinted' par défaut. Le bouton
  // n'apparaît qu'une fois l'analyse chargée : la mutation (Vinted ou VC) dépend de sa plateforme.
  // Masqué sur une analyse VC de profil (sans vestiaireFilter : relance refusée par l'API).
  const parentIsVestiaire = isVestiaireAnalysis(parentAnalysis);
  // parsedFilter sur Vinted, vestiaireFilter sur VC (ids de LA plateforme)
  const parentFilter = analysisCriteriaFilter(parentAnalysis);
  const canSearchSimilar = canSearchSimilarItems(parentAnalysis);
  // « Articles similaires » d'une analyse VC = création d'une analyse VC : verrouillé sans l'accès
  // du plan (grisé, clic → notification d'abonnement). La consultation du feed reste libre.
  const vestiaireLocked = useVestiaireLocked();
  const similarLocked = vestiaireLocked && parentIsVestiaire;
  const [searchText, setSearchText] = useState('');
  // Recherche transmise au serveur après une pause de frappe (une requête par mot, pas par touche)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchText]);
  // sort absent = défaut : pertinence sur une analyse par similarité (le
  // serveur trie déjà ainsi sans sort explicite), sinon plus récent
  const [feedFilters, setFeedFilters] = useState<ProductFilters>({});

  // Analyse par similarité (image, produit source ou description) : le tri
  // « Pertinence » n'a de sens que là (repli silencieux ailleurs)
  const similaritySearch = hasSimilaritySource(parentFilter);
  const defaultSort = similaritySearch ? ('similarity' as const) : ('newest' as const);

  const isStatic = staticProducts !== undefined;
  const { segment } = useAnalysisSegment();

  // ─── Mode feed : pagination serveur (segment + tri + marque serveur) ─────
  const feed = useProductFeedInfinite({
    analysisId: currentAnalysisId,
    status,
    sort: feedFilters.sort ?? defaultSort,
    // Le serveur ne filtre qu'une marque : multi → filtrage client des pages
    brand: feedFilters.brands?.length === 1 ? feedFilters.brands[0] : undefined,
    // La recherche DOIT précéder la pagination : filtrée sur les seules pages chargées, elle rendait
    // une liste vide en tri prix (le mot absent des 50 premiers articles) — bug du 2026-09-16
    searchText: debouncedSearch || undefined,
    segment,
    enabled: !isStatic,
  });

  // Marques réellement présentes (query dédiée, immuable par analyse)
  const { data: brandsData } = useGetAvailableBrandsQuery(
    { analysisId: currentAnalysisId, status },
    { enabled: !isStatic && !!currentAnalysisId, staleTime: Infinity, gcTime: 30 * 60 * 1000 },
  );

  // ─── Mode statique (drawer) : filtres/tri client ─────────────────────────
  const staticData = useProductFeedData({ allProducts: staticProducts ?? [], status });

  const filters = isStatic ? staticData.filters : feedFilters;
  const setFilters = isStatic ? staticData.setFilters : setFeedFilters;
  const availableBrands = isStatic
    ? staticData.availableBrands
    : (brandsData?.getAvailableBrands ?? []).map((row) => ({ brand: row.brand, count: row.count }));

  const baseProducts = isStatic ? staticData.products : feed.products;
  const loading = isStatic ? isLoading : feed.isLoading;

  // Filtrage client : multi-marques (pages déjà chargées) ; recherche texte en mode statique seulement,
  // le mode feed la délègue au serveur
  const filteredProducts = useMemo(() => {
    let filtered = baseProducts;

    if (!isStatic && filters.brands && filters.brands.length > 1) {
      filtered = filtered.filter((product) => product.brand && filters.brands!.includes(product.brand));
    }

    if (isStatic && searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (product) => product.title?.toLowerCase().includes(search) || product.brand?.toLowerCase().includes(search),
      );
    }

    return filtered;
  }, [baseProducts, filters.brands, isStatic, searchText]);

  // « Articles similaires » — recherche FUSIONNÉE serveur (photo + texte de
  // l'annonce source via sourceProductId), critères de l'analyse mère +
  // segment hérités. Rend un ensemble plus large que « même photo » : c'est
  // le comportement nominal (changelog 2026-08-02). Le clic ouvre le popup
  // de confirmation (nom + coût 1 crédit) avant de lancer.
  const [similarTarget, setSimilarTarget] = useState<ProductItemData | null>(null);
  const moveToFolderMutation = useMoveAnalysisToFolderMutation();
  const moveToPositionMutation = useMoveAnalysisToPositionMutation();
  const queryClient = useQueryClient();

  const handleConfirmSimilar = async (name: string, folderId: string | null) => {
    if (!similarTarget) return;
    // Recherche fille : rattachée à la recherche depuis laquelle elle est lancée (même plateforme)
    const parentAnalysisId = currentAnalysisId || undefined;
    const result = parentIsVestiaire
      ? await createVestiaireAnalysis({
          title: name,
          criteria: buildVestiaireSimilarItemCriteria(parentFilter, segment, similarTarget.id),
          parentAnalysisId,
        })
      : await createAnalysisFromCriteria({
          title: name,
          criteria: buildSimilarItemCriteria(parentFilter, segment, similarTarget.id),
          parentAnalysisId,
        });
    if (result.success && result.analysis) {
      // Rangement dans le dossier choisi + épinglage EN TÊTE de sa scope —
      // attendu avant de naviguer pour que l'historique reflète le placement
      // au retour (échec non bloquant : la recherche est créée)
      try {
        if (folderId) {
          await moveToFolderMutation.mutateAsync({ input: { analysisId: result.analysis.id, folderId } });
        }
        await moveToPositionMutation.mutateAsync({ input: { analysisId: result.analysis.id, position: 0 } });
        await queryClient.invalidateQueries({ queryKey: ['GetUserAnalysesPaginated', 'catalog'] });
      } catch {
        // le rangement peut se refaire à la main
      }
      navigate(`/analysis/products/${result.analysis.id}`);
    } else if (!result.success) {
      throw new Error(result.error ?? t('niches.relaunchError'));
    }
  };

  const handleExport = () => {
    const headers = [
      t('ui.csv.title'),
      t('niches.typeBrand'),
      t('segment.price'),
      t('segment.condition'),
      t('folders.favorites'),
      t('ui.csv.url'),
    ];

    const rows = filteredProducts.map((product) => [
      product.title ?? '',
      product.brand ?? '',
      product.price ? product.price.toFixed(2) : '',
      product.itemCondition ?? '',
      product.favouriteCount?.toString() ?? '',
      product.url ?? '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((cell) => (cell.includes(',') || cell.includes('"') ? `"${cell.replace(/"/g, '""')}"` : cell))
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `products_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* Toolbar sur une seule ligne : sélecteur + recherche + marque/tri + export */}
      <div className={css({ display: 'flex', gap: '3', alignItems: 'center', flexWrap: 'wrap' })}>
        {toolbarLeading}

        <SearchBarNew placeholder={t('salesFeed.searchPlaceholder')} value={searchText} onChange={setSearchText} />

        {/* Marques et tri (l'état et le prix sont gérés par la barre de segment) */}
        <FiltersComponent
          filters={filters}
          availableBrands={availableBrands}
          onBrandsChange={(brands) => setFilters({ ...filters, brands })}
          onSortChange={(sort) => setFilters({ ...filters, sort })}
          showSimilaritySort={!isStatic && similaritySearch}
          defaultSort={isStatic ? 'newest' : defaultSort}
        />

        <Button icon="download" handleClick={handleExport} className={css({ ml: 'auto' })}>
          {t('salesFeed.export')}
        </Button>
      </div>

      {/* Nombre de produits (pages chargées) */}
      {/* Feed de produits — cartes façon feed Autocop, scroll infini en mode feed */}
      <ProductSalesFeed
        products={filteredProducts}
        loading={loading}
        onEndReached={
          !isStatic && feed.hasNextPage && !feed.isFetchingNextPage ? () => feed.fetchNextPage() : undefined
        }
        loadingMore={!isStatic && feed.isFetchingNextPage}
        onSearchSimilar={canSearchSimilar ? (product) => setSimilarTarget(product) : undefined}
        similarLocked={similarLocked}
      />

      {/* Confirmation de la relance « articles similaires » (nom + coût) */}
      <RelaunchSearchModal
        isOpen={similarTarget !== null}
        defaultName={similarTarget?.title ?? ''}
        onClose={() => setSimilarTarget(null)}
        onConfirm={handleConfirmSimilar}
      />
    </>
  );
};
